import {
  Box,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BookOpen,
  Eye,
  Sliders,
  Users,
} from "react-feather";
import { FaLightbulb } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { getLucroInsights } from "../../api/expenseBreakdown";
import { customCurrencyFormat } from "../../utils";

const metricStyles = {
  Income: {
    svg: "/fluent-emoji-high-contrast_dollar-banknote.svg",
    gradient: "linear-gradient(180deg, #00B5D8 -34.9%, #065666 112.73%)",
  },
  Expenses: {
    svg: "/ion_receipt.svg",
    gradient: "linear-gradient(180deg, #F56565 -34.9%, #822727 112.73%)",
  },
  Marketing: {
    svg: "/ion_megaphone.svg",
    gradient:
      "linear-gradient(180deg, #00B5D8 -34.9%, #065666 112.73%),linear-gradient(180deg, #627CFB -19.61%, #233DBC 131.76%)",
  },
  Profit: {
    svg: "/healthicons_low-income-level.svg",
    gradient: "linear-gradient(180deg, #38A169 -34.9%, #22543D 112.73%)",
  },
  Team: {
    svg: "/raphael_people.svg",
    gradient: "linear-gradient(180deg, #FFC45B -19.61%, #E65B00 131.76%)",
  },
  Operations: {
    svg: "/bi_gear-fill.svg",
    gradient: "linear-gradient(180deg, #38B2AC -19.61%, #00413E 131.76%)",
  },
  Products: {
    svg: "/fluent-mdl2_product-variant.svg",
    gradient: "linear-gradient(180deg, #EF5BA2 -19.61%, #702459 131.76%)",
  },
};

const tabIcons = {
  Definition: BookOpen,
  Considerations: Eye,
  Levers: Sliders,
  Relationships: Users,
};

const metricInsights = {
  income: {
    Definition: [
      {
        desc: "All income collected from services rendered and products sold to patients. This includes treatment fees, consultations, and any other income generated through the sale of care plans, products, or ancillary services.",
      },
    ],
    Considerations: {
      description: "Key factors to consider when analyzing income.",
      items: [
        {
          title: "Month-to-Month Movement",
          desc: "Has income increased or decreased compared to last month?\n\n Sharp movements may reveal growth opportunities or concerns. Identify whether they result from deliberate actions, such as pricing or marketing changes, or from volatility that requires deeper investigation.",
        },
        {
          title: "Trend Over Time",
          desc: "How does this month compare to previous periods?\n\n Sustained improvement reflects consistent growth and stability, while dips or erratic patterns may suggest operational inefficiencies or seasonal fluctuations that require attention.",
        },
        {
          title: "Volume vs. Value Mix (EHR)",
          desc: "Is the income change driven by patient volume or visit value?\n\n Identifying whether growth is driven by patient volume or visit value clarifies what’s fueling performance and informs smarter decisions on pricing, staffing, and marketing focus.",
        },
        {
          title: "One-Time or Seasonal Effects (Operational)",
          desc: "Were income changes driven by holidays, leave, or unusual events?\n\n Identifying one-off or seasonal factors ensures income performance is interpreted correctly, providing context for fluctuations and preventing short-term anomalies from being mistaken for sustained trends.",
        },
        {
          title: "Collection Efficiency (EHR)",
          desc: "How much of what is billed is actually collected?\n\n Evaluate how well billing converts into collected income. Reliable revenue depends on efficient systems and consistent follow-up that prevent losses from administrative oversight.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve income.",
      items: [
        {
          title: "Refine Pricing Strategy",
          desc: "Evaluate your pricing structure to ensure it reflects the value of care, market positioning, and patient perception. Review average visit fees, care plan pricing, and product markups regularly to maintain competitiveness and profitability without undervaluing your services.",
        },
        {
          title: "Increase Patient Visits",
          desc: "Optimize scheduling, minimize cancellations, and reactivate past patients to boost weekly appointment volume. Track appointment utilization rates and identify scheduling gaps to ensure your team and facility are operating at full capacity.",
        },
        {
          title: "Improve Conversions",
          desc: "Focus on communication, education, and trust to improve treatment plan acceptance and encourage appropriate upgrades to care or product options. Emphasize value and outcomes so patients clearly understand the benefits of committing to recommended care.",
        },
        {
          title: "Enhance Retention and Showups",
          desc: "Keep patients consistently engaged through timely follow ups, appointment reminders, and targeted reactivation efforts. Monitor patient visit frequency and follow through to strengthen long term relationships and stabilize recurring income.",
        },
        {
          title: "Expand Care Options",
          desc: "Develop new care plans or treatment pathways that deepen patient outcomes, increase continuity of care, and create additional revenue opportunities. Introduce structured offerings that align with patient goals and integrate seamlessly into existing operations.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting income.",
      items: [
        {
          title: "Income ↔ Marketing Costs",
          desc: "Marketing directly drives patient flow. Increased marketing spend, when well-targeted, typically leads to higher income through greater patient acquisition and retention.",
          url: "/lucro-metrics/marketing/",
          buttonText: "Explore Marketing",
        },
        {
          title: "Income ↔ Cash on Hand",
          desc: "Strong income improves liquidity, provided expenses and collections are managed effectively. Sustained income growth supports healthy cash reserves.",
        },
        {
          title: "Income ↔ Equity",
          desc: "Higher income, when paired with stable margins, directly boosts profitability and retained earnings, increasing the business’s overall equity value.",
        },
      ],
    },
  },

  team: {
    Definition: [
      {
        desc: "All expenses related to employing and compensating your team, including salaries, commissions, bonuses, payroll taxes, and benefits. Team costs reflect the investment required to deliver patient care and operate the practice effectively.",
      },
    ],
    Considerations: {
      description: "Key factors to consider when analyzing team costs.",
      items: [
        {
          title: "The Perfect P&L Ratio",
          desc: "What is your current team percentage relative to the Perfect P&L?\n\n A higher ratio often signals inefficiency, overstaffing, or underperformance, while a lower ratio may indicate unused capacity or room to grow revenue. Aim to keep the ratio within the benchmark range to balance productivity and profitability.",
        },
        {
          title: "Trend Over Time",
          desc: "How have team costs changed compared to previous periods?\n\n Determine whether team costs are stable, increasing, or decreasing, and assess if those changes result from intentional adjustments such as growth or restructuring, or from underlying inefficiencies that require attention.",
        },
        {
          title: "Operational Variances (Operational)",
          desc: "Were there any unique circumstances this period?\n\n Consider staff absences, new hires or departures, the number of pay runs, and any bonuses or one-time costs. Recognizing these factors helps distinguish genuine performance trends from temporary anomalies.",
        },
        {
          title: "Income and Capacity Alignment (Operational)",
          desc: "Does your team structure align with your income and capacity goals?\n\n Evaluating true capacity helps determine whether adjustments are needed within the team, or if the focus should shift toward driving growth and fully maximizing the efficient use of existing resources.",
        },
        {
          title: "Compensation Structures (Operational)",
          desc: "Do your current compensation structures align with intended outcomes?\n\n A mismatch between pay design and performance expectations often leads to inflated team costs, reduced motivation, and inefficiencies that limit productivity and profitability. Aligning compensation with clear outcomes strengthens performance and cost control.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve team cost efficiency.",
      items: [
        {
          title: "Team Performance Reviews",
          desc: "Evaluate the current performance review structure to ensure consistent tracking of KPIs and alignment between role objectives and expectations. Regular reviews create accountability, identify development needs, and guide performance improvement.",
        },
        {
          title: "Enhance Team Training",
          desc: "Strengthen team performance through focused training and development, including role play, simulations, and coaching. These methods build skill, confidence, and consistency, helping team members improve efficiency and deliver greater value.",
        },
        {
          title: "Incentivize Performance, Not Presence",
          desc: "Structure incentive programs around tangible results such as collections, patient retention, or conversion rates. Reward measurable contribution and goal achievement rather than attendance or tenure to create a culture of accountability and performance.",
        },
        {
          title: "Cross-Train Team Members",
          desc: "Develop flexibility within the team by training staff to manage multiple roles effectively. Cross-training reduces downtime, minimizes workflow bottlenecks, and strengthens business continuity, allowing operations to run smoothly even with limited resources.",
        },
        {
          title: "Automate or Outsource Low-Value Tasks",
          desc: "Use technology or trusted providers to handle repetitive administrative work. Redirecting low-value tasks from in-house staff increases efficiency, reduces payroll strain, and allows the team to focus on care delivery and revenue priorities.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting team costs.",
      items: [
        {
          title: "Team Costs ↔ Income",
          desc: "The team drives income through service delivery and patient experience. Productive, motivated teams generate higher revenue without proportional cost increases.",
          url: "/lucro-metrics/income/",
          buttonText: "Explore Income",
        },
        {
          title: "Team Costs ↔ Profitability",
          desc: "As a major expense, team costs directly impact profit. The right team size, structure, and compensation balance protect margins and stability.",
          url: "/lucro-metrics/profit/",
          buttonText: "Explore Profit",
        },
        {
          title: "Team Costs ↔ Equity",
          desc: "A well-structured, high-performing team is a key business asset. Strong, efficient teams increase the overall value of the practice and build lasting equity.",
        },
      ],
    },
  },
  expense: {
    Definition: [
      {
        title: "Total Expenses Definition",
        desc: "All costs incurred in operating the business, including team, marketing, operational, and product-related expenses. Total expenses represent the outflow required to deliver services and maintain daily operations within a given period.",
      },
    ],
    Considerations: {
      description: "Key factors to consider when analyzing total expenses.",
      items: [
        {
          title: "Month-to-Month Movement",
          desc: "Have total expenses increased or decreased compared to last month?\n\n Identify whether changes are intentional strategic decisions or signs of mismanaged or wasteful spending. Review key cost drivers to confirm whether shifts support business goals or require corrective action.",
        },
        {
          title: "The Perfect P&L Ratio",
          desc: "What percentage of income is consumed by expenses?\n\n A rising expense ratio may indicate operational inefficiencies, unnecessary costs, or underutilized resources. Tracking this ratio helps determine whether revenue growth is generating true profitability or being offset by cost increases.",
        },
        {
          title: "Fixed vs Variable Composition (Operational)",
          desc: "What proportion of expenses are fixed versus variable?\n\n Understanding this mix of fixed costs such as rent and salaries and variable costs like marketing and supplies reveals how much flexibility the business has in managing expenses during slower periods or scaling efficiently during growth.",
        },
        {
          title: "Category Trends",
          desc: "Are certain expense categories rising faster than income?\n\n Identifying which areas such as team, marketing, or product costs are driving cost pressure highlights inefficiencies and helps focus corrective action without compromising performance.",
        },
        {
          title: "Return on Key Expenses (Operational)",
          desc: "Are major cost areas producing measurable returns?\n\n Each significant expense, such as marketing or team costs, should contribute to growth, efficiency, or stability. Evaluate whether spending is generating proportional value or simply sustaining existing activity.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve total expenses.",
      items: [
        {
          title: "Track and Measure ROI Consistently",
          desc: "Assign tracking links, unique numbers, or tags to each campaign. Report new patients, revenue, and conversion by channel monthly. Clear attribution shows what works, supports disciplined budgeting, and prevents spend from drifting into low-return activities.",
        },
        {
          title: "Optimize the Patient Journey",
          desc: "Tighten the path from first contact to first visit. Train the team on scripts, objection handling, and response speed. Standardize follow-up timing and remove friction. Small gains in conversion raise income without extra ad spend.",
        },
        {
          title: "Leverage Referrals and Reactivation",
          desc: "Make referrals and recalls a standing program, not an ad-hoc push. Provide prompts, social proof, and simple incentives. Run monthly reactivation lists with clear outreach steps. These low-cost actions generate quality patients and stabilize revenue.",
        },
        {
          title: "Standardize Monthly Marketing Budget",
          desc: "Set a fixed budget as a percentage of income aligned to growth stage. Allocate first to proven channels, then test smaller portions with clear success criteria. Review results monthly and reweight winners to protect margins and cash flow.",
        },
        {
          title: "Refine Target Audience and Message",
          desc: "Clarify ideal patient segments and the outcomes they value. Build messages that focus on results and experience over discounts. Align offers, visuals, and calls to action with each segment to improve conversion and retention.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting total expenses.",
      items: [
        {
          title: "Total Expenses ↔ Profit",
          desc: "Total expenses have a direct and inverse relationship with profit. Every unnecessary or inflated cost erodes margin, while disciplined expense management strengthens profitability.",
          url: "/lucro-metrics/profit/",
          buttonText: "Explore Profit",
        },
        {
          title: "Total Expenses ↔ Cash on Hand",
          desc: "Higher expenses reduce available cash. Controlling costs without compromising output preserves liquidity and operational stability.",
        },
        {
          title: "Total Expenses ↔ Income",
          desc: "Expenses should scale proportionally with income. If expenses grow faster than income, profit margins compress. Monitoring this ratio protects profitability.",
          url: "/lucro-metrics/income/",
          buttonText: "Explore Income",
        },
      ],
    },
  },
  marketing: {
    Definition: [
      {
        title: "Marketing Definition",
        desc: "All expenses related to attracting new patients and retaining existing ones. This includes internal, external, and digital marketing. Marketing costs should remain within a set percentage of income to balance growth and profitability while ensuring measurable return on spend.",
      },
    ],
    Considerations: {
      description: "Key factors to consider when analyzing marketing costs.",
      items: [
        {
          title: "The Perfect P&L Ratio",
          desc: "How much of total income is allocated to marketing?\n\n A higher ratio often signals poor ROI, marketing inefficiency, or wasteful expenditure that fails to produce results. A lower ratio may indicate under-investment in marketing, leading to missed growth opportunities and reduced patient acquisition momentum.",
        },
        {
          title: "Return on Investment",
          desc: "What financial return does each marketing dollar generate?\n\n Calculate patients and income generated per dollar spent by channel and campaign. Compare results to benchmarks to confirm effectiveness, shifting budget toward consistent ROI and cutting activities that fail to convert into realized revenue.",
        },
        {
          title: "Lead Conversion Rate (EHR)",
          desc: "Are inquiries converting into booked appointments reliably?\n\n Measure inquiry-to-booking conversion by source and team member. Review response time, follow-up, and scripts to remove friction. Strong lead flow without conversion wastes spend; tightening this step increases income without raising marketing costs.",
        },
        {
          title: "Intangible Marketing Activities (Operational)",
          desc: "What non-financial marketing activities are you doing?\n\n Many impactful marketing efforts don’t have a direct financial cost but drive long-term growth. Referrals, patient experience, and wellness plan conversions build reputation, retention, and referral strength that support sustainable income.",
        },
        {
          title: "Channel Performance and Consistency (Operational)",
          desc: "Which channels deliver the best cost per patient with steady results?\n\n Assess cost per patient, show rate, and retention by channel. Favor consistent, repeatable efforts over short spikes. Standardize cadence and follow-up, then scale channels that sustain performance while keeping capacity in mind.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve marketing effectiveness.",
      items: [
        {
          title: "Evaluate and Refine Core Strategies",
          desc: "Identify two or three key strategies that drive consistent results and focus on refining them before expanding. Improve supporting systems, track ROI, and build sustainable growth through disciplined, long-term marketing execution.",
        },
        {
          title: "Establish Marketing Rhythms",
          desc: "Effective marketing requires structure and cadence. Set clear planning, execution, and review cycles for every campaign. Assign ownership, measure performance, and keep a steady rhythm that drives consistent, predictable results over time.",
        },
        {
          title: "Strengthen Sales Effectiveness",
          desc: "Marketing success depends on the ability to convert leads into patients. Improve sales training, communication, and value articulation. Equip your team to build trust, manage objections, and convert more inquiries into booked appointments.",
        },
        {
          title: "Clarify Ideal Patient Ideation",
          desc: "Define precisely who your ideal patient is and what they value most. Clear profiling improves message relevance and ROI. Marketing that speaks to everyone appeals to no one, while clarity attracts the right patients and stronger conversions.",
        },
        {
          title: "Ensure Marketing Balance",
          desc: "Maintain the right balance between internal, external, and digital marketing. Avoid over-reliance on one channel. A diverse mix of strategies builds consistent lead flow, reduces volatility, and supports sustainable growth over time.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting marketing costs.",
      items: [
        {
          title: "Marketing Costs ↔ Income",
          desc: "Effective marketing directly fuels income growth by increasing new patient flow and reactivations. Sustained marketing consistency is key to steady revenue.",
          url: "/lucro-metrics/income/",
          buttonText: "Explore Income",
        },
        {
          title: "Marketing Costs ↔ Cash on Hand",
          desc: "A strong marketing system is a lasting business asset. Consistent, well-structured marketing builds brand strength, patient loyalty, and long-term equity in your practice.",
        },
        {
          title: "Marketing Costs ↔ Cash on Hand",
          desc: "Marketing spend temporarily reduces cash reserves, but strong ROI improves liquidity long-term through higher income inflows.",
        },
      ],
    },
  },
  overhead: {
    Definition: [
      {
        title: "Operational Costs Definition",
        desc: "All expenses related to keeping the business functioning day to day. This includes rent, utilities, consumables, software, insurance, professional services, and other discretionary costs. Operational costs form the infrastructure that sustains care delivery and long-term efficiency.",
      },
    ],
    Considerations: {
      description: "Key factors to consider when analyzing operational costs.",
      items: [
        {
          title: "The Perfect P&L Ratio",
          desc: "What proportion of income is consumed by operational costs?\n\n Rising ratios indicate inefficiency or underutilized spending. Regularly compare operational costs to income to ensure resource use supports growth and profitability without unnecessary overhead expansion.",
        },
        {
          title: "Fixed vs Variable Composition (Operational)",
          desc: "Which costs remain constant and which fluctuate month to month?\n\n Identify fixed expenses like rent and insurance versus variable ones such as supplies or utilities. Understanding this mix reveals flexibility in scaling operations or tightening costs during slower periods.",
        },
        {
          title: "Discretionary vs Essential Spend (Operational)",
          desc: "Are your operational expenses essential or optional?\n\n Distinguish between what drives performance and what is simply convenient. Unchecked discretionary spending drains profit margins. Prioritize resources that directly support patient care and operational efficiency.",
        },
        {
          title: "Trend Stability",
          desc: "Are operational costs stable or increasing over time?\n\n Monitor key categories monthly to detect gradual cost inflation or creeping inefficiencies. Regular reviews prevent small increases from compounding into major overhead strain and preserve long-term stability.",
        },
        {
          title: "Confidence in Operational Structure (Operational)",
          desc: "Are you confident that your operational systems and SOPs are solid?\n\n Weak or inconsistent back-office systems create over-reliance on manual processes, driving higher costs and inefficiency. A structured, documented foundation ensures consistency, scalability, and sustainable operational performance.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve operational efficiency.",
      items: [
        {
          title: "Constantly Review Spend",
          desc: "Regularly review all operational costs to eliminate wasteful or outdated expenses. Ongoing oversight maintains lean operations, ensures resources are allocated efficiently, and preserves profitability across every functional area.",
        },
        {
          title: "Stricter Controls on Recurring Costs",
          desc: "Recurring monthly expenses often become hidden drains on profit. Implement firm controls to confirm which subscriptions, services, and fees are essential, and discontinue those that no longer provide measurable operational value.",
        },
        {
          title: "Automate and Systematize Processes",
          desc: "Use technology to streamline scheduling, billing, and communication workflows. Automation reduces manual errors, improves accuracy, and allows your team to focus on higher-value, patient-facing responsibilities.",
        },
        {
          title: "Define a Discretionary Spending Policy",
          desc: "Establish clear monthly limits and approval processes for non-essential spending. Encourage accountability across the team and ensure every purchase supports operational goals, enhances efficiency, and contributes to overall financial discipline.",
        },
        {
          title: "Explore Alternative Options",
          desc: "Periodically compare key services such as insurance, merchant fees, and billing costs. Evaluating alternative providers or renegotiating terms can lead to meaningful long-term savings without compromising service quality.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting operational costs.",
      items: [
        {
          title: "Operational Costs ↔ Profitability",
          desc: "Operational costs shape profitability. Fixed and recurring expenses drive margin pressure, so keeping costs lean supports strong financial performance.",
          url: "/lucro-metrics/profit/",
          buttonText: "Explore Profit",
        },
        {
          title: "Operational Costs ↔ Cash on Hand",
          desc: "Lowering unnecessary operational outflows boosts liquidity. Controlled spending preserves cash reserves and ensures overall financial stability.",
        },
        {
          title: "Operational Costs ↔ Team Costs",
          desc: "Efficient operations support team productivity. Streamlined systems cut admin time and allow staff to focus on patient care and performance.",
          url: "/lucro-metrics/team/",
          buttonText: "Explore Team",
        },
      ],
    },
  },
  cogs: {
    Definition: [
      {
        title: "Product Costs Definition",
        desc: "All expenses related to purchasing physical products sold or distributed to patients as part of care delivery. This includes supplements, braces, supports, and wellness items. Product costs should stay within defined limits to maintain margin strength and pricing discipline.",
      },
    ],
    Considerations: {
      description: "Key factors to consider when analyzing product costs.",
      items: [
        {
          title: "The Perfect P&L Ratio",
          desc: "How much of total income is spent on products?\n\n Rising percentages may indicate poor pricing discipline, cost inflation, or stock inefficiency. Monitor this ratio closely to protect margins and maintain a healthy balance between product sales and total revenue.",
        },
        {
          title: "Gross Margin Per Product",
          desc: "Are products being sold at sufficient markup?\n\n Track retail price per item and review target margins regularly. If supplier prices rise, adjust retail pricing accordingly to protect profitability and ensure each sale contributes meaningfully to overall income.",
        },
        {
          title: "Stock Turnover and Management (Operational)",
          desc: "Are you managing inventory efficiently?\n\n High stock levels tie up cash and risk waste, while understocking impacts service quality. Maintain accurate reorder systems, monitor expiry dates, and track product movement to balance supply with demand.",
        },
        {
          title: "Supplier Pricing and Consistency (Operational)",
          desc: "Are supplier costs stable and competitive?\n\n Regularly compare suppliers and evaluate contracts to maintain reliability and control pricing. Consistent supplier performance safeguards margins and reduces disruption in product delivery and availability.",
        },
        {
          title: "Product vs Service Balance (Operational)",
          desc: "What is the balance of product sales versus service sales?\n\n Evaluate the current balance between product and service income. Maintaining an appropriate mix supports long-term sustainability by ensuring products enhance, not overshadow, the practice’s core care delivery.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve product cost efficiency.",
      items: [
        {
          title: "Constantly Review Product Pricing",
          desc: "Review product cost prices regularly to identify opportunities for improved terms. Negotiate volume discounts or contract extensions to lower per-unit costs, strengthen supplier relationships, and protect long-term profitability.",
        },
        {
          title: "Refine and Simplify Product Mix",
          desc: "Focus on high-margin, high-demand items that consistently perform. Remove low-return or redundant products that create clutter, tie up cash flow, or add operational complexity without improving patient outcomes.",
        },
        {
          title: "Improve Product Management Systems",
          desc: "Use systems to track product movement, expiry dates, and reorder levels. Proactive control reduces waste, prevents unnecessary cash outflows, and ensures essential products remain available for clinical use.",
        },
        {
          title: "Explore Alternative Product Options",
          desc: "Regularly evaluate new or alternative products that could improve quality, reduce cost, or enhance treatment results. Trialing better options over time can strengthen efficiency, patient satisfaction, and profitability.",
        },
        {
          title: "Integrate Products into Care Plans",
          desc: "Connect products directly to treatment outcomes and patient goals. Embedding product use in care plans enhances compliance, improves results, and creates sustainable revenue that aligns with patient value.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting product costs.",
      items: [
        {
          title: "Product Costs ↔ Income",
          desc: "Higher product sales drive income, but only when margins are maintained. Price discipline and efficient inventory control protect profitability.",
          url: "/lucro-metrics/income/",
          buttonText: "Explore Income",
        },
        {
          title: "Product Costs ↔ Cash on Hand",
          desc: "Excess inventory reduces liquidity. Efficient stock control ensures cash remains available for operations and reserves.",
        },
        {
          title: "Product Costs ↔ Marketing Costs",
          desc: "Selling more products often requires additional marketing support. Understanding this relationship helps balance promotion spend with product profitability.",
          url: "/lucro-metrics/marketing/",
          buttonText: "Explore Marketing",
        },
      ],
    },
  },
  profit: {
    Definition: [
      {
        title: "Profit Definition",
        desc: "Profit represents the financial surplus remaining after all expenses are deducted from income. It reflects the true efficiency and sustainability of your practice. Under the Perfect P&L, profit is the ultimate indicator of performance, margin control, and long-term viability.",
      },
    ],
    Considerations: {
      description: "Key factors to consider when analyzing profit.",
      items: [
        {
          title: "Net Profit Margin",
          desc: "What percentage of income remains as profit after all costs?\n\n This metric reveals true efficiency. A declining margin signals rising costs or reduced pricing power. Regularly review this ratio against benchmarks to ensure profitability supports sustainable business growth.",
        },
        {
          title: "Month-to-Month Profit Movement ",
          desc: "Has profit increased or decreased compared to previous months?\n\n  Identify patterns or sharp fluctuations. Sudden drops often indicate uncontrolled spending, missed targets, or one-time events that need investigation to protect current and future profitability.",
        },
        {
          title: "Expense-to-Income Relationship ",
          desc: "Are expenses scaling proportionally with income growth?\n\n  If costs rise faster than income, margins compress and sustainability declines. Monitor key expense categories and ensure spending aligns with operational efficiency and revenue generation goals.",
        },
        {
          title: "Pricing and Value Alignment (Operational)",
          desc: "Does pricing reflect the value delivered to patients?\n\n  Profitability depends on charging appropriately for outcomes achieved. Review care plans, treatment fees, and value communication to ensure pricing accurately represents your expertise and service quality.",
        },
        {
          title: "Operational Efficiency (Operational)",
          desc: "Is your practice operating efficiently relative to income?\n\n  Strong profit is driven by smooth systems and productive teams. Evaluate workflows, scheduling, and resource use to confirm operations support steady revenue growth and consistent financial returns.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve profitability.",
      items: [
        {
          title: "Enhance Capacity Utilization",
          desc: "Maximize use of available time, space, and team potential. A full, efficient schedule increases income without adding new costs. Identify unused capacity and convert it into billable care to strengthen profit margins sustainably.",
        },
        {
          title: "Optimize Team Structure",
          desc: "Review team composition, roles, and productivity to ensure alignment with patient demand and revenue targets. Streamlined teams with clear accountability and balanced workloads deliver higher performance and profitability.",
        },
        {
          title: "Increase Patient Flow",
          desc: "Boost patient volume through targeted marketing, internal referrals, and consistent reactivation efforts. Expanding total visits spreads fixed costs more efficiently, improving income stability and overall profitability.",
        },
        {
          title: "Refine Operational Efficiency",
          desc: "Tighten systems, workflows, and recurring costs to reduce waste and enhance productivity. Incremental operational improvements compound over time, lowering expenses and strengthening long-term profitability.",
        },
        {
          title: "Expand with Intention",
          desc: "Pursue growth only when capacity, team, and structure are fully optimized. Strategic, data-led expansion protects margins, minimizes risk, and increases both sustainability and long-term equity value.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting profit.",
      items: [
        {
          title: "Profit ↔ Total Expenses",
          desc: "Profit is the inverse of total expenses. Reducing unnecessary or wasteful spending directly improves net margins and overall financial performance.",
          url: "/lucro-metrics/expense/",
          buttonText: "Explore Expenses",
        },
        {
          title: "Profit ↔ Cash on Hand",
          desc: "The level of profitability determines the surplus cash available to store in the business. Strong profits steadily build liquidity and financial resilience.",
        },
        {
          title: "Profit ↔ Equity",
          desc: "Sustained profitability drives equity growth through retained earnings. Each profitable month strengthens long-term business value and owner wealth.",
        },
      ],
    },
  },
};

export default function LucroInsightButton({
  metricName = "Income",
  customer_id,
  formattedDate,
  selectedTag,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [metricSummary, setMetricSummary] = useState(null);
  const [sparkData, setSparkData] = useState([]);
  const adjusted = JSON.parse(
    sessionStorage.getItem("adjustedView") || "false"
  );
  const currentMetric = metricStyles[metricName];

  const [ytd, setYtd] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("ytd") || "false");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = () => {
      const ytd = JSON.parse(sessionStorage.getItem("ytd") || "false");
      setYtd(ytd);
    };

    window.addEventListener("ytd-changed", handler);
    return () => window.removeEventListener("ytd-changed", handler);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchSummary = async () => {
      try {
        const res = await getLucroInsights(
          customer_id,
          formattedDate,
          selectedTag,
          controller.signal,
          { adjusted, ytd }
        );

        if (isMounted && res?.data) {
          const data = res.data;
          setMetricSummary(data);
          setSparkData(data.sparkData || []);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Error fetching metric summary:", err);
        }
      }
    };

    if (customer_id && formattedDate && selectedTag) {
      fetchSummary();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, selectedTag, adjusted, ytd]);

  const navigate = useNavigate();
  const tabPanelScrollRef = useRef(null);

  return (
    <>
      <Button
        leftIcon={<FaLightbulb />}
        bg="#536DEC"
        color="white"
        _hover={{ bg: "#3E56C5" }}
        borderRadius="8px"
        px={4}
        py={2}
        fontWeight="500"
        onClick={onOpen}
        fontSize={"16px"}
        width={"100%"}
        maxWidth={"141px"}
      >
        Lucro Insight
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered>
        <ModalOverlay />
        <ModalContent
          maxWidth="640px"
          maxHeight="650px"
          borderRadius="16px"
          overflow="hidden"
          p="0"
          display="flex"
          flexDirection="column"
        >
          <ModalCloseButton />

          <ModalHeader p={0} flexShrink={0}>
            <VStack align="flex-start" mt="24px" spacing="16px">
              <Stack
                px="24px"
                direction={{ base: "column", md: "row" }}
                spacing={{ base: 3, md: 3 }}
                align={{ base: "flex-start", md: "center" }}
              >
                <Box
                  w="64px"
                  h="64px"
                  borderRadius="8px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="#E6E9EC"
                  background={currentMetric?.gradient || "#536DEC14"}
                  border={`1px solid ${
                    currentMetric ? "transparent" : "#536DEC"
                  }`}
                  boxShadow="0px 1px 12px 0px #00000014"
                >
                  {currentMetric?.svg ? (
                    <img
                      src={currentMetric.svg}
                      alt={metricName}
                      width="32"
                      height="32"
                      style={{ objectFit: "contain" }}
                    />
                  ) : (
                    <Text color="#6B7280">?</Text>
                  )}
                </Box>

                <VStack align="flex-start" spacing={1} mt={{ base: 2, md: 0 }}>
                  <Text fontSize="14px" color="#6B7280" fontWeight="500">
                    {metricName}
                  </Text>

                  <Stack
                    direction={{ base: "column", md: "row" }}
                    spacing={{ base: 2, md: 2 }}
                    align={{ base: "flex-start", md: "center" }}
                  >
                    <Text fontSize="28px" fontWeight="700">
                      {customCurrencyFormat(metricSummary?.total ?? 0)}
                    </Text>

                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      mt={{ base: "4px", md: "10px" }}
                      color={
                        Number.isFinite(
                          metricSummary?.current_target_change_pct
                        )
                          ? ["income", "profit"].includes(selectedTag)
                            ? metricSummary.current_target_change_pct >= 0
                              ? "green.500"
                              : "red.500"
                            : metricSummary.current_target_change_pct >= 0
                            ? "red.500"
                            : "green.500"
                          : "gray.500"
                      }
                      display="flex"
                      alignItems="center"
                      gap="4px"
                    >
                      {Number.isFinite(
                        metricSummary?.current_target_change_pct
                      ) ? (
                        <>
                          {metricSummary.current_target_change_pct >= 0 ? (
                            <ArrowUp size={14} />
                          ) : (
                            <ArrowDown size={14} />
                          )}
                          {metricSummary.current_target_change_pct >= 0
                            ? "+"
                            : ""}
                          {metricSummary.current_target_change_pct.toFixed(2)}%
                          from target
                        </>
                      ) : (
                        ""
                      )}
                    </Text>
                  </Stack>
                </VStack>
              </Stack>

              <Box
                w="100%"
                h="80px"
                sx={{
                  "& svg:focus, g:focus, path:focus": {
                    outline: "none !important",
                  },
                }}
              >
                <ResponsiveContainer width="100%" height={80}>
                  <ComposedChart
                    data={sparkData}
                    margin={{ top: 6, right: 0, left: 0, bottom: 6 }}
                  >
                    <defs>
                      <linearGradient
                        id="sparklineGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="rgba(99, 91, 255, 0.2)" />
                        <stop offset="100%" stopColor="rgba(99, 91, 255, 0)" />
                      </linearGradient>
                    </defs>

                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="none"
                      fill="url(#sparklineGradient)"
                      baseValue="dataMin"
                      isAnimationActive={false}
                    />

                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#635BFF"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                      isAnimationActive={false}
                    />

                    <Tooltip
                      cursor={false}
                      content={({ active, payload }) => {
                        if (!active || !payload || !payload.length) return null;
                        const dataPoint = payload[0].payload;
                        return (
                          <Box
                            bg="white"
                            px={3}
                            py={1}
                            borderRadius="6px"
                            boxShadow="0 2px 6px rgba(0,0,0,0.15)"
                            fontSize="12px"
                            fontWeight="500"
                          >
                            {`${dataPoint.x}: ${customCurrencyFormat(
                              dataPoint.value
                            )}`}
                          </Box>
                        );
                      }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </VStack>
            <Divider />
          </ModalHeader>

          <ModalBody
            p="20px"
            display="flex"
            flexDirection="column"
            flex="1"
            overflow="hidden"
            css={{
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                background: "#A0AEC0",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": { background: "#718096" },
            }}
          >
            <Tabs
              isFitted
              variant="unstyled"
              colorScheme="blue"
              display="flex"
              flexDirection="column"
              flex="1"
              overflow="hidden"
            >
              <Box
                bg="#E6E9EC"
                p="4px"
                mb="10px"
                borderRadius="full"
                flexShrink={0}
              >
                <TabList
                  display="flex"
                  flexDirection="row"
                  overflowX={{ base: "auto", md: "hidden" }}
                  overflowY="hidden"
                  whiteSpace="nowrap"
                  gap={1}
                  flex={{ base: "0 0 auto", md: 1 }}
                  css={{
                    "&::-webkit-scrollbar": { display: "none" },
                    "-ms-overflow-style": "none",
                    "scrollbar-width": "none",
                  }}
                >
                  {metricInsights[selectedTag] ? (
                    Object.keys(metricInsights[selectedTag]).map((tab, idx) => {
                      const IconComponent = tabIcons[tab];
                      return (
                        <Tab
                          key={idx}
                          flex={{ base: "0 0 auto", md: 1 }}
                          borderRadius="full"
                          fontWeight="500"
                          fontSize="14px"
                          py="8px"
                          height="28px"
                          color="black"
                          bg="transparent"
                          _selected={{
                            bg: "white",
                            color: "black",
                            fontWeight: "600",
                          }}
                          _hover={{ bg: "white" }}
                          transition="background 0.2s ease"
                          onClick={() => {
                            if (tabPanelScrollRef.current) {
                              tabPanelScrollRef.current.scrollTop = 0;
                            }
                          }}
                        >
                          <HStack spacing={2} justify="center">
                            {IconComponent && <IconComponent size={14} />}
                            <Text>{tab}</Text>
                          </HStack>
                        </Tab>
                      );
                    })
                  ) : (
                    <Text px={4} color="#6B7280">
                      No insights available for this metric
                    </Text>
                  )}
                </TabList>
              </Box>

              <TabPanels
                flex="1"
                overflowY="auto"
                px={0}
                ref={tabPanelScrollRef}
              >
                {metricInsights[selectedTag] ? (
                  Object.entries(metricInsights[selectedTag]).map(
                    ([tab, data]) => {
                      const items = Array.isArray(data)
                        ? data
                        : data.items || [];
                      const description = data.description || "";

                      return (
                        <TabPanel key={tab} px={0} pb={0}>
                          <HStack mb={2} align="center">
                            <Box
                              w="4px"
                              h="24px"
                              borderRadius="2px"
                              bgGradient="linear(to-r, #121621 1.24%, #536DEC 117.97%)"
                            />
                            <Text fontSize="18px" fontWeight="700" ml={2}>
                              {tab}
                            </Text>
                          </HStack>

                          {description && (
                            <Text fontSize="14px" color="#6B7280" mb={4}>
                              {description}
                            </Text>
                          )}

                          <VStack align="stretch" spacing={4}>
                            {items.length > 0 ? (
                              items.map((item, idx) =>
                                tab === "Definition" ? (
                                  <Box
                                    key={idx}
                                    p={3}
                                    borderRadius="12px"
                                    background="#EDF3FF"
                                    border="1px solid #97B1FF"
                                    boxShadow="sm"
                                  >
                                    <Text
                                      fontSize="15px"
                                      color="#1A202C"
                                      whiteSpace="pre-line"
                                    >
                                      {item.desc}
                                    </Text>
                                  </Box>
                                ) : (
                                  <HStack
                                    key={idx}
                                    p={3}
                                    borderRadius="12px"
                                    border="1px solid #0000001A"
                                    boxShadow="sm"
                                    spacing={3}
                                    align="start"
                                  >
                                    <VStack
                                      w="40px"
                                      h="40px"
                                      bgGradient="linear(to-r, #121621 1.24%, #536DEC 117.97%)"
                                      borderRadius="14px"
                                      justify="center"
                                    >
                                      <Text
                                        fontSize="20px"
                                        fontWeight="600"
                                        color="white"
                                      >
                                        {idx + 1}
                                      </Text>
                                    </VStack>

                                    <VStack
                                      align="stretch"
                                      spacing={1}
                                      flex="1"
                                    >
                                      <Text fontWeight="600" color="#1A1A1A">
                                        {item.title}
                                      </Text>
                                      <Text
                                        fontSize="14px"
                                        color="#6B7280"
                                        mb={2}
                                        whiteSpace="pre-line"
                                      >
                                        {item.desc}
                                      </Text>

                                      {item.url && (
                                        <HStack align="start">
                                          <Button
                                            h="32px"
                                            w="auto"
                                            bg="#536DEC"
                                            color="white"
                                            rightIcon={<ArrowRight size={16} />}
                                            onClick={() => navigate(item.url)}
                                            _hover={{ bg: "#455FDE" }}
                                            _active={{ bg: "#455FDE" }}
                                          >
                                            {item.buttonText}
                                          </Button>
                                        </HStack>
                                      )}
                                    </VStack>
                                  </HStack>
                                )
                              )
                            ) : (
                              <Text color="#6B7280">
                                No items available for this section.
                              </Text>
                            )}
                          </VStack>
                        </TabPanel>
                      );
                    }
                  )
                ) : (
                  <TabPanel>
                    <Text color="#6B7280">
                      No insights available for this metric.
                    </Text>
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
